import { MVTLoader } from "@loaders.gl/mvt";
import type { BinaryFeatureCollection } from "@loaders.gl/schema";
import { getUrl } from "aws-amplify/storage";
import { TileLoadProps } from "common/types/TileLoadProps";
import { MVTLayer } from "deck.gl";
import { PMTiles } from "pmtiles";

export class CustomPMTilesMVTLayer<
  ExtraProps extends object
> extends MVTLayer<ExtraProps> {
  private _mockGML?: boolean;
  private static pmtilesCache: { [key: string]: Promise<PMTiles> } = {};

  set mockGML(value: boolean) {
    this._mockGML = value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getPMTilesPath(z: number): string {
    return "noTile.pmtiles";
  }

  private async getPMTilesInstance(path: string): Promise<PMTiles> {
    const cache = CustomPMTilesMVTLayer.pmtilesCache[path];

    if (cache) {
      return cache;
    }

    const promise = (async () => {
      let url: string | undefined;

      if (this._mockGML) {
        url = `http://localhost:20099/${path}`;
      } else {
        try {
          const result = await getUrl({
            path: `public/${path}`,
            options: { expiresIn: 24 * 60 * 60 } // 24時間有効な署名付きURLを取得
          });
          url = result.url.href;
        } catch (e) {
          console.error(`Failed to get signed URL for ${path}`, e);
          throw e;
        }
      }

      if (!url) throw new Error(`Invalid URL for ${path}`);
      return new PMTiles(url);
    })();

    CustomPMTilesMVTLayer.pmtilesCache[path] = promise;
    return promise;
  }

  async getTileData(
    loadProps: TileLoadProps
  ): Promise<BinaryFeatureCollection> {
    const { index } = loadProps;
    const { z, x, y } = index;

    try {
      const filePath = this.getPMTilesPath(z);
      const pmtiles = await this.getPMTilesInstance(filePath);

      const response = await pmtiles.getZxy(z, x, y);

      // データがない場合は null を返さず Reject する
      if (!response) {
        return Promise.reject("No content");
      }

      const loadOptions = this.getLoadOptions();

      const parsed = await MVTLoader.parse(response.data, {
        ...loadOptions,
        mvt: {
          ...loadOptions?.mvt,
          coordinates: this.context.viewport.resolution ? "wgs84" : "local",
          tileIndex: index
        },
        gis: { format: "binary" }
      });

      return parsed as BinaryFeatureCollection;
    } catch (err) {
      // エラー時も Promise.reject で返す
      // console.warn(`Tile fetch failed: ${z}/${x}/${y}`, err);
      return Promise.reject(err);
    }
  }
}
